import { MAIN_STYLE, MAIN_STYLE_GROUPS, PADDING_SIDES, PRESS_EVENTS } from '../shared.js'

// The gauge's scale (the tick band around the dial) — main's own scale_*
// properties, shown in their own Property Editor accordion instead of the
// general list. Property Editor metadata only: these stay ordinary main
// properties everywhere else (resolution, defaults, rendering, export).
// Declared here rather than in shared.js because the group is gauge's — the
// property NAMES are shared with linemeter, but the grouping is not something
// every widget with a scale_* property should inherit automatically.
const SCALE_GROUP = {
    id: 'scale',
    label: 'Scale',
    properties: [
        'scale_width',
        'scale_border_width',
        'scale_end_line_width',
        'scale_end_border_width',
        'scale_grad_color',
        'scale_end_color',
    ],
}

export const gauge = {

    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS, SCALE_GROUP],
            properties: {
                ...MAIN_STYLE,
                line_color: { type: 'color' },
                line_width: { type: 'int', min: 0 },
                text_font_family:   { type: 'string', internal: true },
                // text_color intentionally stays here, NOT moved to major_ticks, even though
                // firmware actually reads the scale-label text style from LV_GAUGE_PART_MAJOR
                // (lv_gauge.c:552) — HaspGauge.js's canvas rendering currently reads
                // this.style('main').text_color directly (HaspGauge.js:279). Relocating this
                // requires a matching HaspGauge.js change; deferred to the Phase 4 pass so the
                // schema fix and the widget-code fix land together. See FIRMWARE-PARITY-AUDIT.md.
                text_color: { type: 'color' },
                padding: { type: 'int', min: 0, exportSpecial: true },
                ...PADDING_SIDES,
                keep_circle: { type: 'bool', internal: true },
                scale_width: { type: 'int', min: 0 },
                scale_border_width: { type: 'int', min: 0, label: 'scale border width' },
                scale_end_line_width: { type: 'int', min: 0, label: 'scale end line width' },
                scale_end_border_width: { type: 'int', min: 0, label: 'scale end border width' },
                scale_grad_color: { type: 'color' },
                scale_end_color: { type: 'color' },
            },
        },
        indicator: {
            label: 'Indicator',
            exportSuffix: '10',
            order: 1,
            properties: {
                bg_color: { type: 'color' },
                line_rounded: { type: 'bool' },
                line_color: { type: 'color' },
                line_width: { type: 'int', min: 0 },
                line_opa: { type: 'int', min: 0, max: 255, internal: true },
                line_dash_width: { type: 'int', min: 0, label: 'line dash width' },
                line_dash_gap: { type: 'int', min: 0, label: 'line dash gap' },
                border_width: { type: 'int', min: 0 },
                border_color: { type: 'color' },
                border_opa: { type: 'int', min: 0, max: 255, internal: true },
                radius: { type: 'int', min: 0, max: 999 },
                size: { type: 'int', min: 0, label: 'pivot size' },
                // pad_inner is a single real LVGL style property (needle length offset from
                // center) — NOT the 4-side "padding" exportSpecial convention used by main/knob
                // (ObjectExport.js only special-cases those two part names); exported generically
                // as pad_inner10 like any other style property.
                pad_inner: { type: 'int', min: 0, label: 'needle length offset' },
                keep_circle: { type: 'bool', internal: true },
            },
        },
        major_ticks: {
            label: 'Major Ticks',
            exportSuffix: '60',
            order: 2,
            properties: {
                line_width: { type: 'int', min: 0 },
                scale_width: { type: 'int', min: 0 },
                line_color: { type: 'color' },
                scale_grad_color: { type: 'color' },
                scale_end_color: { type: 'color' },
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid: { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        val: { type: 'int' }, min: { type: 'int' }, max: { type: 'int' },
        // Optional object-level dataset, following Chart/Legend's resource
        // convention. It is authoritative for effective needles when set;
        // manual needle data remains stored as the reversible fallback.
        dataset: { type: 'resource', resourceType: 'dataset', label: 'Dataset', internal: true },
        // Designer-side ordered LVGL needle collection. It intentionally has
        // no default: legacy Gauges must continue to serialize as `val` only.
        // HaspGauge.objectExport() owns the explicit array serialization so
        // internal Designer IDs never reach JSONL.
        needles: { type: 'array', label: 'Needles' },
        critical_value: { type: 'int' },
        value_str: { type: 'string' },
        // Pixel size for the selected Value Font Family (see `value_font_resource`,
        // below) — exported verbatim as `value_size` when a resource is selected,
        // see Exporter._processFonts().
        value_font: { type: 'int', min: 8, label: 'Value Size (px)' },
        value_font_resource: { type: 'resource', resourceType: 'font', label: 'Value Font Family', inline: true },
        value_color: { type: 'color' },
        value_ofs_x: { type: 'int', label: 'value offset x' },
        value_ofs_y: { type: 'int', label: 'value offset y' },
        // verbatim as `text_size` regardless — see Exporter._processFonts().
        text_font: { type: 'int', min: 8, label: 'Size (px)' },
        angle: { type: 'int', min: 0, max: 360, unit: '°', hasp: true },
        rotation: { type: 'int', min: 0, max: 360, unit: '°', hasp: true },
        label_count: { type: 'int', min: 0 },
        line_count: { type: 'int', min: 0 },
        format: { type: 'int', min: 0, max: 4 },
        decimals: { type: 'int', min: 0, max: 6, label: 'decimals' },
        entityid: { type: 'string', internal: true },
        stretch_mode: {
            type: 'string', internal: true, default: 'keep_ratio',
            options: ['none', 'keep_ratio', 'v_stretch', 'h_stretch', 'full'],
        },
        font:     { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 200, height: 200, min_width: 100, min_height: 10,
            enabled: true, hidden: false,
            min: 0, max: 100, val: 0, value_str: '',
            rotation: 0, angle: 270,
            critical_value: 80, line_count: 21, label_count: 6, format: 0, decimals: 0,
            // Label rendering
            text_font: 32, font: '',
            value_font_resource: '',
        },
        styles: {
            // Part defaults — required by drawLines geometry calculations. Values matched
            // against lv_theme_hasp.c's gauge_init() (HASP_DPX(n) == n at reference DPI 160).
            main: {
                padding: 15, scale_width: 10, line_width: 2, scale_border_width: 0,
                scale_end_line_width: 2, scale_end_border_width: 3,
            },
            // Indicator (needle pivot) — circle shape, white fill
            indicator: {
                bg_color: '#ffffff', radius: 999, border_width: 0,
                line_width: 8, size: 30, pad_inner: 10,
            },
            major_ticks: { scale_width: 20, line_width: 8 },
        },
    },
    states: ['pressed', 'disabled'],
    editorCapabilities: ['can_transform', 'can_copy', 'has_objectmenu', 'keep_ratio'],
    runtimeCapabilities: ['has_data'],

    events: PRESS_EVENTS,

    bindings: {
        properties: { val: {}, min: {}, max: {}, value_str: {} },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'automation', 'climate', 'light', 'switch', 'esphome'] },
        events: {},
    },
}
